import { cn } from '@invent/wl-ui-kit-next';
import {
  Button,
  Checkbox,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle
} from '@invent/wl-ui-kit-next/ui';
import { History, X } from 'lucide-react';
import * as React from 'react';

import { ChoiceSelect, conflictWords, HelpPopover, NotPermitted, StatusChip } from '@/design';
import { ApiError, ProblemType } from '@/domains/api';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import {
  DESIGN_FONTS,
  DESIGN_WIDTH,
  type IMailDesign,
  type IMailDesignColors,
  type IMailDesignSettings,
  type IMailDesignVersion,
  type MailDesignCode
} from '../../api/mail-design-contracts';
import { mailDesignsApi } from '../../api/mail-designs-api';
import type { IMailPlaceholder, IRichPart } from '../../api/mail-template-contracts';
import { mailTemplatesApi } from '../../api/mail-templates-api';
import { AdminPage, LoadError, TableSkeleton, useAdminData, useDebounced } from '../../components/admin-page';
import { refusalMessage } from '../../refusal';
import { EditorLookProvider, lookOf } from './design-look';
import { PREVIEW_DEFAULTS, PreviewBody, Segmented, type IPreviewOptions, type IPreviewState } from './mail-preview';
import { RichField } from './placeholder-field';
import { PublishDialog, type IPublishChoice } from './publish-dialog';
import { docKey } from './rich-part';
import { useDraftSaving, type DraftAnswer } from './draft-saving';
import { formatEt, versionStamp } from './template-fields';
import { HISTORY_ROOM } from './template-history';
import { useTemplateTabs } from './template-nav';

/**
 * Setup → Email templates → Design (SR-523 stage 6): the frame every mail is drawn in. The
 * `veritant` design's settings — the band, the colours, the font, the width and a footer of small
 * print — on the left, a template drawn with them on the right (survey-api's design preview), a
 * draft that saves itself and Publish… as a template has them (`draft-saving.tsx`), and History.
 * `plain` has nothing to set: its mails are the body alone. A template's version names its design
 * (Sender and design); bodies say sentinel colours survey-api swaps for the design's, so what is
 * published here reaches every mail at once.
 */

export function MailDesignPage() {
  const can = useCan();
  const tabs = useTemplateTabs();
  if (!can('mail.templates')) {
    return (
      <AdminPage title="Email templates">
        <NotPermitted what="editing the email design" />
      </AdminPage>
    );
  }
  return (
    <AdminPage title="Email templates" tabs={tabs} actions={<DesignHelp />}>
      <DesignView />
    </AdminPage>
  );
}

function DesignHelp() {
  return (
    <HelpPopover label="About the email design" align="end">
      <p>
        The frame every email is drawn in: the band at the top, the card, the colours and the font
        of the text, links and buttons, the width, and small print at the bottom of every email.
        A template picks its design under Sender and design.
      </p>
      <p>
        <em>Veritant</em> is the one set here. <em>Plain</em> draws the body alone — no band, no
        card — for an email that should read as a personal note; it has nothing to set.
      </p>
      <p>
        Your work is kept as a draft as you change it (Save ▾ → Autosave drafts; off, Save draft):
        no email changes. Publish… makes it the next version, now or from a date and time (ET), and
        every email sent from then on is drawn with it — the templates&apos; texts stay as they are.
      </p>
    </HelpPopover>
  );
}

const PLACEHOLDERS: readonly IMailPlaceholder[] = [
  {
    name: 'support_contact',
    description: 'Whom to contact with questions: the deployment’s contact line',
    sample: 'Questions? Write to surveys@example.com.',
    group: 'Mail',
    kind: 'text'
  },
  {
    name: 'postal_address',
    description: 'The postal address bulk mail carries (Mail:Bulk:PostalAddress)',
    sample: 'Veritant, 499 Washington Blvd, Jersey City, NJ 07310',
    group: 'Mail',
    kind: 'text'
  }
];

const NO_CONDITIONS: never[] = [];

const COLOR_FIELDS: readonly { key: keyof IMailDesignColors; label: string; help: string }[] = [
  { key: 'text', label: 'Text', help: 'Paragraphs and lists.' },
  { key: 'heading', label: 'Headings', help: 'Headings in the body.' },
  { key: 'accent', label: 'Links and buttons', help: 'Links, buttons and the summary’s rule.' },
  { key: 'muted', label: 'Small print', help: 'Small print, labels and the footer.' },
  { key: 'pageBackground', label: 'Page', help: 'Around the card.' },
  { key: 'card', label: 'Card', help: 'Behind the body.' },
  { key: 'border', label: 'Lines', help: 'Rules and the card’s edge.' },
  { key: 'summaryBackground', label: 'Summary', help: 'Behind a submission’s summary table.' }
];

const HEX = /^#[0-9a-f]{6}$/i;

type Problems = Partial<Record<string, string>>;

/** Why survey-api would refuse the settings, by field (`colors.accent`) — said before it is asked. */
function localProblems(settings: IMailDesignSettings): Problems {
  const problems: Problems = {};
  const color = (key: string, value: string) => {
    if (!HEX.test(value)) problems[key] = 'A colour as #rrggbb.';
  };
  color('band.background', settings.band.background);
  color('band.color', settings.band.color);
  for (const { key } of COLOR_FIELDS) color(`colors.${key}`, settings.colors[key]);
  const logo = settings.band.logoUrl.trim();
  if (logo && !/^https:\/\/\S+$/i.test(logo)) problems['band.logoUrl'] = 'An https address.';
  if (settings.width < DESIGN_WIDTH.min || settings.width > DESIGN_WIDTH.max) {
    problems.width = `Between ${DESIGN_WIDTH.min} and ${DESIGN_WIDTH.max} pixels.`;
  }
  return problems;
}

/** survey-api's `problems[]` by field: `settings.colors.accent` → `colors.accent`. */
function refusedProblems(error: unknown): { byField: Problems; other: Nullable<string> } {
  if (!(error instanceof ApiError) || error.status !== 400) return { byField: {}, other: null };
  const entries = [
    ...(error.problem?.problems ?? []),
    ...Object.entries(error.problem?.errors ?? {}).map(([target, reasons]) => ({ target, reason: reasons.join(' ') }))
  ];
  const byField: Problems = {};
  const other: string[] = [];
  for (const { target, reason } of entries) {
    const key = target.replace(/^settings\./i, '').replace(/^\w/, (first) => first.toLowerCase());
    if (key) byField[key] = byField[key] ? `${byField[key]} ${reason}` : reason;
    else other.push(reason);
  }
  return { byField, other: other.length ? other.join(' ') : null };
}

function settingsKey(settings: IMailDesignSettings): string {
  return JSON.stringify({ ...settings, footer: docKey(settings.footer.doc) });
}

function sameSettings(a: IMailDesignSettings, b: IMailDesignSettings): boolean {
  return settingsKey(a) === settingsKey(b);
}

function DesignView() {
  const read = useAdminData((signal) => mailDesignsApi.get('veritant', signal), []);
  const [generation, setGeneration] = React.useState(0);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  if (read.error) return <LoadError message={read.error} />;
  if (!read.data) return <TableSkeleton rows={6} />;
  return (
    <DesignEditor
      key={generation}
      design={read.data}
      said={said}
      onAdopt={read.adopt}
      onRefetch={async () => {
        await read.reload();
      }}
      onReload={async (message) => {
        setSaid(message);
        await read.reload();
        setGeneration((n) => n + 1);
      }}
    />
  );
}

type Busy = Nullable<'draft' | 'publish' | 'withdraw' | 'discard'>;

function DesignEditor({
  design,
  said,
  onAdopt,
  onRefetch,
  onReload
}: {
  design: IMailDesign;
  said: Nullable<string>;
  onAdopt: (design: IMailDesign) => void;
  /** The design read again, the form kept as it stands — *Reload* after a conflict. */
  onRefetch: () => Promise<void>;
  onReload: (message: Nullable<string>) => Promise<void>;
}) {
  const [which, setWhich] = React.useState<MailDesignCode>('veritant');
  const draft = design.draft;
  const saved = draft?.settings ?? design.settings;
  const [settings, setSettings] = React.useState<IMailDesignSettings>(saved);
  const [restored, setRestored] = React.useState<Nullable<number>>(null);
  const [busy, setBusy] = React.useState<Busy>(null);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [conflict, setConflict] = React.useState<Nullable<{ text: string; alert: boolean }>>(null);
  const [saveProblems, setSaveProblems] = React.useState<Problems>({});
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [publishRefusal, setPublishRefusal] = React.useState<Nullable<string>>(null);
  const [suggesting, setSuggesting] = React.useState(false);
  const footerHandle = React.useRef<Nullable<{ flush?: () => Promise<void>; pending?: () => boolean }>>(null);
  const latestSettings = React.useRef(settings);
  latestSettings.current = settings;
  const [edits, setEdits] = React.useState(0);
  // The draft as of now, moved the moment a save lands: a save started by a timer quotes the
  // revision the last one left.
  const designNow = React.useRef(design);
  designNow.current = design;
  const draftNow = React.useRef(draft);
  draftNow.current = draft;
  const savedNow = React.useRef(saved);
  savedNow.current = saved;

  const changed = !sameSettings(settings, saved);
  const local = localProblems(settings);
  const problems: Problems = { ...saveProblems, ...local };
  const current = design.versions.find((version) => version.version === design.current) ?? null;
  const scheduled = design.versions.find((version) => version.status === 'scheduled') ?? null;
  const latest = Math.max(0, ...design.versions.map((version) => version.version));
  const idle = busy === null && !suggesting;
  const canSave = changed && idle && Object.keys(local).length === 0;
  const canPublish = (changed || draft !== null) && idle && Object.keys(local).length === 0;
  const look = React.useMemo(() => lookOf(settings), [settings]);

  const update = (next: IMailDesignSettings) => {
    latestSettings.current = next;
    setSettings(next);
    setEdits((n) => n + 1);
    setRestored(null);
  };
  const set = <K extends keyof IMailDesignSettings>(key: K, value: IMailDesignSettings[K]) => {
    update({ ...latestSettings.current, [key]: value });
    setSaveProblems((was) => Object.fromEntries(Object.entries(was).filter(([field]) => !field.startsWith(key))));
  };

  /** The footer's last edit compiled, so what is saved is what the editor shows. */
  const flushed = async (): Promise<IMailDesignSettings> => {
    await footerHandle.current?.flush?.();
    return latestSettings.current;
  };

  const refused = (error: unknown, fallback: string): Nullable<string> => {
    if (error instanceof ApiError && error.isProblem(ProblemType.revisionConflict)) {
      setConflict({ text: conflictWords('design'), alert: true });
      return null;
    }
    const { byField, other } = refusedProblems(error);
    if (Object.keys(byField).length > 0 || other) {
      setSaveProblems(byField);
      return other ?? 'Some settings have problems: see the fields.';
    }
    return refusalMessage(error, fallback);
  };

  /** The settings as the form holds them, under the revision the last save left. */
  const putDraft = async (next: IMailDesignSettings, note: string) => {
    const kept = await mailDesignsApi.saveDraft('veritant', draftNow.current?.revision ?? 0, { settings: next, note });
    draftNow.current = kept;
    savedNow.current = kept.settings;
    designNow.current = { ...designNow.current, draft: kept };
    onAdopt(designNow.current);
  };

  const saveDraft = async (): Promise<DraftAnswer> => {
    setBusy('draft');
    setRefusal(null);
    setConflict(null);
    setSaveProblems({});
    try {
      await putDraft(await flushed(), draftNow.current?.note ?? '');
      setRestored(null);
      return 'landed';
    } catch (error) {
      const message = refused(error, 'The draft could not be saved.');
      setRefusal(message);
      return message === null ? 'conflict' : 'refused';
    } finally {
      setBusy((was) => (was === 'draft' ? null : was));
    }
  };

  const publish = async (choice: IPublishChoice) => {
    setBusy('publish');
    // An autosave already out lands first — its end leaves this busy alone — and the draft below
    // quotes the revision it leaves.
    await saving.settle();
    setPublishRefusal(null);
    setConflict(null);
    setSaveProblems({});
    try {
      const next = await flushed();
      if (!draftNow.current || !sameSettings(next, savedNow.current)) await putDraft(next, choice.note);
      await mailDesignsApi.publish('veritant', latest, choice);
      setPublishOpen(false);
      saving.quiet();
      await onReload(
        choice.effectiveAt
          ? `Version ${latest + 1} of the design is scheduled for ${formatEt(choice.effectiveAt)}.`
          : `Published as version ${latest + 1}: every email sent from now on is drawn with it.`
      );
    } catch (error) {
      const message = refused(error, 'The design could not be published.');
      if (message) setPublishRefusal(message);
      else setPublishOpen(false);
    } finally {
      setBusy(null);
    }
  };

  const withdraw = (version: IMailDesignVersion) => {
    if (!window.confirm(`Withdraw version ${version.version}? It will not take effect.`)) return;
    // The form is read afresh after: edits owed are saved first, or asked.
    saving.leave(() => void withdrawNow(version));
  };
  const withdrawNow = async (version: IMailDesignVersion) => {
    setBusy('withdraw');
    setRefusal(null);
    try {
      await mailDesignsApi.withdraw('veritant', version.version);
      saving.quiet();
      await onReload(`Version ${version.version} withdrawn: it will not take effect.`);
    } catch (error) {
      setRefusal(refusalMessage(error, 'The version could not be withdrawn.'));
    } finally {
      setBusy(null);
    }
  };

  const discardDraft = async () => {
    const revision = draftNow.current?.revision;
    if (revision === undefined) return;
    setBusy('discard');
    setRefusal(null);
    try {
      await mailDesignsApi.discardDraft('veritant', revision);
      saving.quiet();
      await onReload('Draft discarded.');
    } catch (error) {
      setRefusal(refusalMessage(error, 'The draft could not be discarded.'));
    } finally {
      setBusy(null);
    }
  };

  const discard = () => {
    update(savedNow.current);
    setSaveProblems({});
    setRefusal(null);
  };

  const restore = (version: IMailDesignVersion) => {
    update(version.settings);
    setRestored(version.version);
    setSaveProblems({});
    setHistoryOpen(false);
  };

  const saving = useDraftSaving({
    changed,
    isChanged: () => !sameSettings(latestSettings.current, savedNow.current),
    pending: () => footerHandle.current?.pending?.() === true,
    flushEdits: flushed,
    hasDraft: draft !== null,
    draftAt: draft?.updatedAt ?? null,
    savable: Object.keys(local).length === 0 && !suggesting,
    flushable: Object.keys(local).length === 0 && !suggesting,
    canSave,
    canPublish,
    busy,
    edits,
    // The band, the colours, the font, the width and the footer, each one.
    count: () => {
      const part = (of: IMailDesignSettings, key: keyof IMailDesignSettings) =>
        key === 'footer' ? docKey(of.footer.doc) : JSON.stringify(of[key]);
      return (Object.keys(latestSettings.current) as (keyof IMailDesignSettings)[]).filter(
        (key) => part(latestSettings.current, key) !== part(savedNow.current, key)
      ).length;
    },
    refusal,
    save: saveDraft,
    onPublish: () => {
      setPublishRefusal(null);
      setPublishOpen(true);
    },
    onDiscardChanges: discard,
    onDiscardDraft: () => void discardDraft(),
    discardDraftWords: 'The draft is deleted and the form goes back to the live version, with every change since.'
  });

  /** *Reload* after a conflict: the design as saved now, the form's changes kept, unsaved. */
  const reloadKept = async () => {
    await onRefetch();
    saving.resume();
    setConflict({ text: 'The design as saved now is read — your changes are still here, unsaved.', alert: false });
  };

  const plain = which === 'plain';

  return (
    <EditorLookProvider value={look}>
      <div className={cn('grid min-w-0 gap-4', historyOpen && HISTORY_ROOM)} data-mail-design="veritant">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md border border-border bg-white px-4 py-2.5">
          <Segmented
            label="Design"
            value={which}
            onChange={setWhich}
            choices={[
              { value: 'veritant', label: 'Veritant' },
              { value: 'plain', label: 'Plain' }
            ]}
          />
          {!plain && (
            <>
              <span className="text-xs text-muted-foreground" data-current-version="">
                {current && current.version > 0
                  ? versionStamp(current.version, current.effectiveAt ?? current.createdAt, null)
                  : 'Default'}
              </span>
              {restored !== null ? (
                <StatusChip tone="info">
                  <span role="status">{restored === 0 ? 'Default' : `Version ${restored}`} restored, not saved</span>
                </StatusChip>
              ) : null}
              <div className="ml-auto flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  aria-expanded={historyOpen}
                  onClick={() => setHistoryOpen((open) => !open)}
                >
                  <History className="size-4" aria-hidden />
                  History
                </Button>
                {saving.controls}
              </div>
            </>
          )}
        </div>

        {said && (
          <p className="text-sm" role="status" data-mail-said="">
            {said}
          </p>
        )}
        {!plain && scheduled && (
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-[color-mix(in_srgb,var(--color-teal)_35%,white)] bg-[color-mix(in_srgb,var(--color-turquoise)_8%,white)] px-4 py-2 text-sm"
            data-mail-scheduled={scheduled.version}
          >
            <span>
              <span className="font-medium">Version {scheduled.version}</span> is scheduled for{' '}
              {formatEt(scheduled.effectiveAt ?? '')}.
              {scheduled.note && <span className="text-muted-foreground"> {scheduled.note}</span>}
            </span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto"
              disabled={busy !== null}
              onClick={() => void withdraw(scheduled)}
            >
              Withdraw
            </Button>
          </div>
        )}
        {conflict && (
          <div
            className={cn('flex flex-wrap items-center gap-2 text-sm', conflict.alert ? 'text-destructive' : 'text-muted-foreground')}
            role={conflict.alert ? 'alert' : 'status'}
            data-mail-conflict=""
          >
            <span>{conflict.text}</span>
            {conflict.alert && (
              <Button type="button" size="sm" variant="outline" onClick={() => void reloadKept()}>
                Reload
              </Button>
            )}
          </div>
        )}
        {refusal && (
          <p className="text-sm text-destructive" role="alert">
            {refusal}
          </p>
        )}

        <div className="grid min-w-0 items-start gap-6 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)]">
          {plain ? (
            <section
              className="grid gap-2 rounded-md border border-border bg-white p-4 text-[0.8125rem]"
              data-design-plain=""
            >
              <h2 className="text-base font-semibold text-[var(--color-royal-blue)]">Plain</h2>
              <p>
                The body alone: no band, no card and no design footer — an email that reads as a
                personal note. Bulk mail still carries its unsubscribe footer.
              </p>
              <p className="text-muted-foreground">Nothing to set here.</p>
            </section>
          ) : (
            <SettingsForm
              fontStacks={design.fonts}
              settings={settings}
              problems={problems}
              onSet={set}
              onSuggesting={setSuggesting}
              footerHandle={(handle) => {
                footerHandle.current = handle;
              }}
            />
          )}
          <DesignPreview code={which} settings={settings} />
        </div>

        <DesignHistory
          open={historyOpen}
          onOpenChange={setHistoryOpen}
          versions={design.versions}
          current={design.current}
          busy={busy !== null}
          onRestore={restore}
          onWithdraw={(version) => void withdraw(version)}
        />
        <PublishDialog
          open={publishOpen}
          onOpenChange={setPublishOpen}
          title="The Veritant design"
          scheduled={scheduled}
          initialNote={draft?.note ?? ''}
          busy={busy === 'publish'}
          refusal={publishRefusal}
          onPublish={(choice) => void publish(choice)}
        />
        {saving.dialogs}
      </div>
    </EditorLookProvider>
  );
}

/** A section's heading, its "?" beside it. */
function SectionHead({ id, title, help }: { id: string; title: string; help?: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1">
      <h3 id={id} className="text-[0.8125rem] font-semibold text-[var(--color-royal-blue)]">
        {title}
      </h3>
      {help && (
        <HelpPopover label={`About ${title.toLowerCase()}`} className="size-6">
          {help}
        </HelpPopover>
      )}
    </span>
  );
}

function SettingsForm({
  fontStacks,
  settings,
  problems,
  onSet,
  onSuggesting,
  footerHandle
}: {
  /** survey-api's own list; ours where it gives none. */
  fontStacks: readonly string[];
  settings: IMailDesignSettings;
  problems: Problems;
  onSet: <K extends keyof IMailDesignSettings>(key: K, value: IMailDesignSettings[K]) => void;
  onSuggesting: (open: boolean) => void;
  footerHandle: (handle: Nullable<{ flush?: () => Promise<void>; pending?: () => boolean }>) => void;
}) {
  const id = React.useId();
  const { band, colors } = settings;
  const offered = fontStacks.length
    ? fontStacks.map((stack) => ({ value: stack, label: stack.split(',')[0].trim() }))
    : DESIGN_FONTS;
  const fonts = offered.some((font) => font.value === settings.font)
    ? offered
    : [...offered, { label: settings.font, value: settings.font }];
  const problem = (key: string) =>
    problems[key] && (
      <p id={`${id}-${key}-problem`} className="text-xs text-destructive">
        {problems[key]}
      </p>
    );
  return (
    <section
      aria-label="Design settings"
      className="grid min-w-0 gap-5 rounded-md border border-border bg-white p-4 text-[0.8125rem]"
      data-design-settings=""
    >
      <fieldset className="grid gap-2.5" aria-labelledby={`${id}-band`}>
        <SectionHead
          id={`${id}-band`}
          title="Band"
          help={
            <p>
              The strip across the top of every email: words or a logo on a colour. A logo is an
              https address on one of the allowed image hosts; it stands in for the words.
            </p>
          }
        />
        <label className="flex w-fit items-center gap-2" data-design-band-show="">
          <Checkbox checked={band.show} onCheckedChange={(next) => onSet('band', { ...band, show: next === true })} />
          Show the band
        </label>
        {band.show && (
          <>
            <TextField
              id={`${id}-band-text`}
              label="Words"
              value={band.text}
              max={80}
              problem={problems['band.text']}
              onChange={(text) => onSet('band', { ...band, text })}
            />
            <TextField
              id={`${id}-band-logo`}
              label="Logo address"
              value={band.logoUrl}
              placeholder="https://… (optional)"
              max={500}
              problem={problems['band.logoUrl']}
              onChange={(logoUrl) => onSet('band', { ...band, logoUrl })}
            />
            <div className="grid gap-2.5 sm:grid-cols-2">
              <ColorField
                id={`${id}-band-background`}
                label="Background"
                value={band.background}
                problem={problems['band.background']}
                onChange={(background) => onSet('band', { ...band, background })}
              />
              <ColorField
                id={`${id}-band-color`}
                label="Words"
                value={band.color}
                problem={problems['band.color']}
                onChange={(color) => onSet('band', { ...band, color })}
              />
            </div>
          </>
        )}
      </fieldset>

      <fieldset className="grid gap-2.5 border-t border-border pt-4" aria-labelledby={`${id}-colors`}>
        <SectionHead
          id={`${id}-colors`}
          title="Colours"
          help={
            <ul className="grid gap-1">
              {COLOR_FIELDS.map((field) => (
                <li key={field.key}>
                  <em>{field.label}</em>: {field.help}
                </li>
              ))}
            </ul>
          }
        />
        <div className="grid gap-2.5 sm:grid-cols-2">
          {COLOR_FIELDS.map((field) => (
            <ColorField
              key={field.key}
              id={`${id}-color-${field.key}`}
              label={field.label}
              value={colors[field.key]}
              problem={problems[`colors.${field.key}`]}
              onChange={(value) => onSet('colors', { ...colors, [field.key]: value })}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="grid gap-2.5 border-t border-border pt-4" aria-labelledby={`${id}-type`}>
        <SectionHead id={`${id}-type`} title="Type and width" />
        <div className="grid gap-1">
          <span id={`${id}-font`} className="font-medium">
            Font
          </span>
          <ChoiceSelect
            labelledBy={`${id}-font`}
            value={settings.font}
            onChange={(font) => onSet('font', font)}
            choices={fonts}
            className="h-8"
          />
          {problem('font')}
        </div>
        <div className="grid gap-1">
          <span className="flex items-baseline justify-between">
            <label htmlFor={`${id}-width`} className="font-medium">
              Width
            </label>
            <span className="text-xs text-muted-foreground tabular-nums">{settings.width} px</span>
          </span>
          <input
            id={`${id}-width`}
            type="range"
            min={DESIGN_WIDTH.min}
            max={DESIGN_WIDTH.max}
            step={10}
            value={settings.width}
            aria-valuetext={`${settings.width} pixels`}
            onChange={(event) => onSet('width', Number(event.target.value))}
            className="w-full accent-[var(--color-teal)]"
            data-design-width=""
          />
          {problem('width')}
        </div>
      </fieldset>

      <div className="grid gap-1.5 border-t border-border pt-4" data-design-footer="">
        <SectionHead
          id={`${id}-footer`}
          title="Footer"
          help={
            <p>
              Small print at the bottom of every email in this design, under the body — empty, none.
              Bulk mail adds its unsubscribe line below it.
            </p>
          }
        />
        <div className="mail-design-footer">
          <style>{FOOTER_CSS}</style>
          <RichField
            id={`${id}-footer-field`}
            labelledBy={`${id}-footer`}
            value={settings.footer}
            placeholders={PLACEHOLDERS}
            conditions={NO_CONDITIONS}
            invalid={Boolean(problems.footer)}
            onChange={(footer: IRichPart) => onSet('footer', footer)}
            onFocus={() => {}}
            onBlur={() => {}}
            onSuggesting={onSuggesting}
            handle={footerHandle}
            smallPrint
          />
        </div>
        {problem('footer')}
      </div>
    </section>
  );
}

/** The footer is small print: the editor draws it so. */
const FOOTER_CSS = '.mail-design-footer .mail-rich .ProseMirror{font-size:13px;line-height:19px;color:var(--mail-muted)}';

function TextField({
  id,
  label,
  value,
  placeholder,
  max,
  problem,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  placeholder?: string;
  max: number;
  problem?: string;
  onChange: (value: string) => void;
}) {
  return (
    <div className="grid gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      <Input
        id={id}
        value={value}
        maxLength={max}
        placeholder={placeholder}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? `${id}-problem` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className="h-8"
      />
      {problem && (
        <p id={`${id}-problem`} className="text-xs text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}

/** A colour as a swatch that opens the browser's picker, and its `#rrggbb` to type. */
function ColorField({
  id,
  label,
  value,
  problem,
  onChange
}: {
  id: string;
  label: string;
  value: string;
  problem?: string;
  onChange: (value: string) => void;
}) {
  const valid = HEX.test(value);
  return (
    <div className="grid gap-1" data-design-color={label}>
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      <span className="flex items-center gap-2">
        <input
          type="color"
          value={valid ? value.toLowerCase() : '#000000'}
          onChange={(event) => onChange(event.target.value)}
          aria-label={`${label}: pick a colour`}
          className="size-8 shrink-0 cursor-pointer rounded-md border border-input bg-white p-0.5"
        />
        <Input
          id={id}
          value={value}
          maxLength={7}
          spellCheck={false}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? `${id}-problem` : undefined}
          // survey-api keeps a colour in small letters: typed in capitals, it is the same colour.
          onChange={(event) => onChange(event.target.value.trim().toLowerCase())}
          className="h-8 font-mono text-xs uppercase"
        />
      </span>
      {problem && (
        <p id={`${id}-problem`} className="text-xs text-destructive">
          {problem}
        </p>
      )}
    </div>
  );
}

/** A template drawn with the settings as they stand: survey-api's design preview. */
function DesignPreview({ code, settings }: { code: MailDesignCode; settings: IMailDesignSettings }) {
  const templateId = React.useId();
  const templates = useAdminData(
    async (signal) => (await mailTemplatesApi.list(signal)).filter((template) => !template.archivedAt),
    []
  );
  const [template, setTemplate] = React.useState('submission_confirmation');
  const [options, setOptions] = React.useState<IPreviewOptions>(PREVIEW_DEFAULTS);
  const asked = useDebounced(JSON.stringify({ code, template, settings }), 400);
  const [state, setState] = React.useState<IPreviewState>({ shown: null, problems: {}, other: null, pending: true });

  React.useEffect(() => {
    const controller = new AbortController();
    const request = JSON.parse(asked) as { code: MailDesignCode; template: string; settings: IMailDesignSettings };
    // The settings say what they would refuse before they are sent.
    if (Object.keys(localProblems(request.settings)).length > 0) {
      setState((now) => ({ ...now, other: 'No preview until the settings are fixed.', pending: false }));
      return;
    }
    setState((now) => ({ ...now, pending: true }));
    mailDesignsApi
      .preview(
        request.code,
        { settings: request.settings, template: request.template, role: 'submitter', attached: true },
        controller.signal
      )
      .then((shown) => setState({ shown, problems: {}, other: null, pending: false }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        const refused = refusedProblems(error);
        setState((now) => ({
          ...now,
          other:
            refused.other ??
            (Object.keys(refused.byField).length > 0
              ? Object.entries(refused.byField)
                  .map(([field, reason]) => `${field}: ${reason}`)
                  .join(' ')
              : refusalMessage(error, 'The preview could not be drawn.')),
          pending: false
        }));
      });
    return () => controller.abort();
  }, [asked]);

  const choices = (templates.data ?? []).map((entry) => ({ value: entry.code, label: entry.name }));
  return (
    <section
      aria-labelledby={`${templateId}-heading`}
      className="flex min-w-0 flex-col gap-3 rounded-md border border-border bg-white p-4 lg:sticky lg:top-[calc(var(--compact-bar-height,48px)+1rem)] lg:h-[calc(100vh-var(--compact-bar-height,48px)-2rem)]"
      data-design-preview=""
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[0.8125rem]">
        <h2 id={`${templateId}-heading`} className="text-base font-semibold text-[var(--color-royal-blue)]">
          Preview
        </h2>
        <span className="flex min-w-0 flex-[1_1_14rem] items-center gap-2">
          <span id={templateId} className="text-xs whitespace-nowrap text-muted-foreground">
            Drawn with
          </span>
          <ChoiceSelect
            labelledBy={templateId}
            value={template}
            onChange={setTemplate}
            choices={
              choices.some((choice) => choice.value === template)
                ? choices
                : [{ value: template, label: 'Submission confirmation' }, ...choices]
            }
            className="h-8 min-w-0 flex-1"
          />
        </span>
        <Segmented
          label="Width"
          value={options.device}
          onChange={(device) => setOptions({ ...options, device })}
          choices={[
            { value: 'desktop', label: 'Desktop' },
            { value: 'mobile', label: 'Mobile' }
          ]}
        />
        <Segmented
          label="Format"
          value={options.format}
          onChange={(format) => setOptions({ ...options, format })}
          choices={[
            { value: 'html', label: 'HTML' },
            { value: 'text', label: 'Plain text' }
          ]}
        />
      </div>
      <PreviewBody state={state} options={options} fill blocked="No preview until the settings are fixed." />
    </section>
  );
}

/** Every published version of the design, newest first, the default last: restore one into the
 *  form, or withdraw one still scheduled. */
function DesignHistory({
  open,
  onOpenChange,
  versions,
  current: currentVersion,
  busy,
  onRestore,
  onWithdraw
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versions: readonly IMailDesignVersion[];
  current: number;
  busy: boolean;
  onRestore: (version: IMailDesignVersion) => void;
  onWithdraw: (version: IMailDesignVersion) => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-[min(26rem,calc(100vw-2rem))] gap-0 p-0"
        onInteractOutside={(event) => event.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
        data-design-history=""
      >
        <SheetHeader className="px-5 pt-5 pb-4">
          <SheetTitle className="text-[0.9375rem] text-[var(--color-royal-blue)]">History</SheetTitle>
          <SheetDescription>Every published version of the design, newest first; the default last.</SheetDescription>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="ml-auto size-8"
            aria-label="Close history"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </Button>
        </SheetHeader>
        <ul className="grid content-start gap-1 overflow-y-auto p-3 text-[0.8125rem]" aria-label="Versions">
          {versions.length === 0 && <li className="px-3 text-muted-foreground">Only the default so far.</li>}
          {versions.map((version) => {
            const current = version.version === currentVersion;
            return (
              <li
                key={version.version}
                className="grid gap-1 rounded-md border border-border px-3 py-2"
                data-design-version={version.version}
              >
                <span className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium text-[var(--color-royal-blue)] tabular-nums">
                    {version.version === 0 ? 'Default' : `Version ${version.version}`}
                  </span>
                  {current && <StatusChip tone="success">Current</StatusChip>}
                  {version.status === 'scheduled' && <StatusChip tone="info">Scheduled</StatusChip>}
                  {version.status === 'withdrawn' && <StatusChip tone="neutral">Withdrawn</StatusChip>}
                </span>
                {version.version !== 0 && (
                  <span className="text-xs text-muted-foreground">
                    {[version.createdAt && formatDateTime(version.createdAt), version.createdBy?.email]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                )}
                {version.status === 'scheduled' && version.effectiveAt && (
                  <span className="text-xs text-muted-foreground">Takes effect {formatEt(version.effectiveAt)}</span>
                )}
                {version.note && <span className="line-clamp-3 text-xs whitespace-pre-line">{version.note}</span>}
                <span className="flex flex-wrap gap-2 pt-1">
                  {!current && version.status !== 'withdrawn' && (
                    <Button type="button" size="sm" variant="outline" onClick={() => onRestore(version)}>
                      {version.version === 0 ? 'Restore default' : 'Restore this version'}
                    </Button>
                  )}
                  {version.status === 'scheduled' && (
                    <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => onWithdraw(version)}>
                      Withdraw
                    </Button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
